import { logException } from '@apps/main/modules/plugins/logException';

import type { Coordinate } from '@libs/maps';

interface IpPositionResponse {
  ip: string;
  city: string;
  region: string;
  region_code: string;
  country_code: string;
  country_code_iso3: string;
  country_name: string;
  country_capital: string;
  country_tld: string;
  continent_code: string;
  in_eu: boolean;
  postal: string;
  latitude?: number;
  longitude?: number;
  timezone: string;
  utc_offset: string;
  country_calling_code: string;
  currency: string;
  currency_name: string;
  languages: string;
  asn: string;
  org: string;
}

export class IpApiService {
  private readonly ipApiURL = 'https://ipapi.co/json/';
  private readonly errorMessage = `Failed to fetch IP position from: ${this.ipApiURL}!`;

  public async getUserCoordinatesFromIPApi(
    tries = 5
  ): Promise<Coordinate | undefined> {
    try {
      performance.mark('fetch');
      const retriedResponse = await this.fetchRetry(this.ipApiURL, tries);

      if (!retriedResponse.response.ok) {
        logException(this.errorMessage, {
          status: retriedResponse.response.status,
        });
        return;
      }

      const data: IpPositionResponse = await retriedResponse.response.json();

      if (data.latitude && data.longitude) {
        return { latitude: data.latitude, longitude: data.longitude };
      }

      return undefined;
    } catch (error) {
      performance.mark('error');
      const totalTimeToError = performance.measure('fetchToError', {
        start: 'fetch',
        end: 'error',
      }).duration;

      logException(this.errorMessage, {
        error,
        totalTimeToError,
      });
    }
  }

  private async fetchRetry(
    url: string,
    tries: number,
    delay = 400
  ): Promise<{ response: Response; tries: number }> {
    let triesLeft = tries;

    const onError = async (
      error: unknown
    ): Promise<{ response: Response; tries: number }> => {
      triesLeft -= 1;

      if (triesLeft <= 0) {
        return Promise.reject(new Error(String(error)));
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
      return await this.fetchRetry(url, triesLeft, delay * 2);
    };

    try {
      const response = await fetch(url);

      return { response, tries: triesLeft };
    } catch (error) {
      return onError(error);
    }
  }
}
